import GraduateUserContext, { EGraduateUserUiState } from '../../contexts/GraduateUserContext';
import GlobalContext from '../../contexts/GlobalContext';
import Card from '../Card/Card';
import PasswordInput from '../PasswordInput/PasswordInput';
import PasswordValidator from '../PasswordValidator/PasswordValidator';
import LegalAgreements from '../LegalAgreements/LegalAgreements';
import AlreadyHaveAccount from '../AlreadyHaveAccount/AlreadyHaveAccount';
import styles from './GraduateUserForm.module.scss';
import common from '../../util/scss/common.module.scss';
import { Alert, MenuItem, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FC, useContext } from 'react';

const GraduateUserForm: FC = () => {
    const { language, countriesList } = useContext(GlobalContext);
    const {
        graduateUserUiState,
        firstName,
        setFirstName,
        firstNameIsValid,
        firstNameValidationMessage,
        lastName,
        setLastName,
        lastNameIsValid,
        lastNameValidationMessage,
        email,
        password,
        setPassword,
        showPasswordValidation,
        setShowPasswordValidation,
        countryCode,
        setCountryCode,
        submitGraduateUser,
        disableSubmit,
        errorMessage,
    } = useContext(GraduateUserContext);

    const handlePasswordFocus = () => {
        !showPasswordValidation && setShowPasswordValidation(true);
    };

    const handleSubmitGraduateUser = (event) => {
        event.preventDefault();
        submitGraduateUser();
    };

    return (
        <>
            <Card>
                <header>
                    <h1 className={common.pageHeader}>{language.createAccount.header}</h1>
                </header>
                {graduateUserUiState === EGraduateUserUiState.softError && (
                    <section className={common.genericError}>
                        <Alert severity="error">{errorMessage}</Alert>
                    </section>
                )}
                <form data-testid="graduate-user-form">
                    <main className={styles.graduateUserForm}>
                        <TextField
                            label={language.createAccount.form.firstName}
                            value={firstName}
                            error={!firstNameIsValid}
                            helperText={!firstNameIsValid && firstNameValidationMessage}
                            onChange={(event) => setFirstName(event.target.value)}
                            fullWidth
                            margin="normal"
                        />
                        <TextField
                            label={language.createAccount.form.lastName}
                            value={lastName}
                            error={!lastNameIsValid}
                            helperText={!lastNameIsValid && lastNameValidationMessage}
                            onChange={(event) => setLastName(event.target.value)}
                            fullWidth
                            margin="normal"
                        />
                        <TextField
                            label={language.createAccount.form.email}
                            value={email}
                            disabled
                            fullWidth
                            margin="normal"
                        />
                        <PasswordInput
                            value={password}
                            label={language.createAccount.form.password}
                            onChange={(event) => setPassword(event.target.value)}
                            onFocus={handlePasswordFocus}
                            autoFocus={true}
                        />
                        <section
                            className={`${styles.passwordValidatorBlock} ${showPasswordValidation && styles.show}`}
                        >
                            <div className={styles.passwordValidator}>
                                <PasswordValidator password={password} />
                            </div>
                        </section>
                        <TextField
                            select
                            label={language.createAccount.form.country}
                            value={countryCode}
                            onChange={(event) => setCountryCode(event.target.value)}
                            margin="normal"
                            fullWidth
                        >
                            {countriesList.map((country) => (
                                <MenuItem key={country.code} value={country.code}>
                                    {country.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <section className={styles.graduateUserCTA}>
                            <LoadingButton
                                variant="contained"
                                type="submit"
                                disabled={disableSubmit || graduateUserUiState === EGraduateUserUiState.loading}
                                onClick={handleSubmitGraduateUser}
                                loading={graduateUserUiState === EGraduateUserUiState.loading}
                                size="large"
                            >
                                {language.createAccount.createAccountCTA}
                            </LoadingButton>
                        </section>
                        <LegalAgreements />
                    </main>
                </form>
            </Card>
            <AlreadyHaveAccount />
        </>
    );
};

export default GraduateUserForm;
